import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { AssetDetailRecord, AssetKind } from '@shared/types';
import { subscribeToShellDeleteRequests } from '@renderer/app/events/shell-delete-events';
import { useShellStore } from '@renderer/app/state/shell-store';
import { EditorBodyLayout, EditorHeroCard, EditorPageLayout, EditorSectionCard } from '@renderer/shared/components/editor-scaffold';
import { Icon } from '@renderer/shared/components/icons';
import { EmptyState, OverflowMenuButton, SelectField, TextField, ValuePair } from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { useRegisterEditorSaveHandler } from '@renderer/shared/hooks/use-register-editor-save-handler';
import { deleteAssets, getAssetById, updateAsset } from './resources-repository';

interface AssetDraft {
  name: string;
  kind: AssetKind;
  projectId: string;
  customerId: string;
  mimeType: string;
  storagePath: string;
}

function toDraft(detail: AssetDetailRecord): AssetDraft {
  return {
    name: detail.asset.name,
    kind: detail.asset.kind,
    projectId: detail.asset.projectId ?? '',
    customerId: detail.asset.customerId ?? '',
    mimeType: detail.asset.mimeType ?? '',
    storagePath: detail.asset.storagePath ?? ''
  };
}

export function AssetEditorScreen(): JSX.Element {
  const navigate = useNavigate();
  const { assetId } = useParams();
  const { t } = useI18n();
  const setRouteDetailTitle = useShellStore((state) => state.setRouteDetailTitle);
  const [detail, setDetail] = useState<AssetDetailRecord | null>(null);
  const [draft, setDraft] = useState<AssetDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!assetId) {
      setError('Missing asset id.');
      return;
    }

    let active = true;
    void getAssetById(assetId)
      .then((nextDetail) => {
        if (!active) {
          return;
        }

        if (!nextDetail) {
          setError('Asset not found.');
          return;
        }

        setDetail(nextDetail);
        setDraft(toDraft(nextDetail));
        setRouteDetailTitle(nextDetail.asset.name);
      })
      .catch((nextError) => {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : 'Asset unavailable.');
        }
      });

    return () => {
      active = false;
      setRouteDetailTitle(null);
    };
  }, [assetId, setRouteDetailTitle]);

  useEffect(
    () =>
      subscribeToShellDeleteRequests((detailRequest) => {
        if (detailRequest.domain !== 'resources' || !detail || !detailRequest.ids.includes(detail.asset.id)) {
          return;
        }

        void deleteAssets({ ids: [detail.asset.id] }).then(() => navigate('/resources'));
      }),
    [detail, navigate]
  );

  const isDirty =
    detail && draft
      ? draft.name !== detail.asset.name ||
        draft.kind !== detail.asset.kind ||
        draft.projectId !== (detail.asset.projectId ?? '') ||
        draft.customerId !== (detail.asset.customerId ?? '') ||
        draft.mimeType !== (detail.asset.mimeType ?? '') ||
        draft.storagePath !== (detail.asset.storagePath ?? '')
      : false;

  useRegisterEditorSaveHandler(
    detail && draft
      ? () => {
          if (isSaving) {
            return;
          }

          if (!isDirty) {
            return;
          }

          void handleSave();
        }
      : null
  );

  const projectOptions = useMemo(() => {
    if (!detail) {
      return [{ value: '', label: t('common.notSet') }];
    }

    return [
      { value: '', label: t('common.notSet') },
      ...detail.projectSummaries.map((project) => ({ value: project.id, label: project.name }))
    ];
  }, [detail, t]);

  const customerOptions = useMemo(() => {
    if (!detail) {
      return [{ value: '', label: t('common.notSet') }];
    }

    return [
      { value: '', label: t('common.notSet') },
      ...detail.customerSummaries.map((customer) => ({ value: customer.id, label: customer.name }))
    ];
  }, [detail, t]);

  const kindOptions = useMemo(
    () => [
      { value: 'logo', label: t('enums.assetKind.logo') },
      { value: 'icon', label: t('enums.assetKind.icon') },
      { value: 'document', label: t('enums.assetKind.document') },
      { value: 'image', label: t('enums.assetKind.image') },
      { value: 'pdf', label: t('enums.assetKind.pdf') }
    ],
    [t]
  );

  if (error) {
    return (
      <div className="av-page">
        <EmptyState title={t('navigation.resources')} description={error} />
      </div>
    );
  }

  if (!detail || !draft) {
    return <div className="av-page" />;
  }

  async function handleSave(): Promise<void> {
    if (!assetId) {
      return;
    }

    if (!draft) {
      return;
    }

    setSaveError(null);
    setIsSaving(true);

    try {
      const nextDetail = await updateAsset({
        id: assetId,
        name: draft.name.trim(),
        kind: draft.kind,
        projectId: draft.projectId || null,
        customerId: draft.customerId || null,
        mimeType: draft.mimeType.trim() || null,
        storagePath: draft.storagePath.trim() || null
      });

      setDetail(nextDetail);
      setDraft(toDraft(nextDetail));
    } catch (nextError) {
      setSaveError(nextError instanceof Error ? nextError.message : 'Save failed.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel(): void {
    setSaveError(null);
    if (!detail) {
      return;
    }

    setDraft(toDraft(detail));
  }

  async function handleDelete(): Promise<void> {
    if (!detail) {
      return;
    }

    if (!window.confirm(t('common.confirmDeleteSoft'))) {
      return;
    }

    await deleteAssets({ ids: [detail.asset.id] });
    navigate('/resources');
  }

  return (
    <EditorPageLayout
      hero={
        <EditorHeroCard
          actions={
            <>
              <button className="av-secondary-button" type="button" onClick={() => void handleDelete()}>
                <Icon name="trash" />
                <span>{t('shell.delete')}</span>
              </button>
              <OverflowMenuButton />
            </>
          }
        >
          <div className="av-editor-hero-card__content">
            <div className="av-editor-hero-card__identity">
              <div className="av-editor-hero-card__icon">
                <Icon name="resources" />
              </div>
              <div className="av-editor-hero-card__identity-copy">
                <h2>{detail.asset.name}</h2>
                <div className="av-editor-hero-card__meta">
                  <ValuePair label={t('quickAdd.fields.category')} value={t(`enums.assetKind.${detail.asset.kind}`)} />
                </div>
              </div>
            </div>
          </div>
        </EditorHeroCard>
      }
    >
      <EditorBodyLayout
        main={
          <>
            {saveError ? <div className="av-inline-error">{saveError}</div> : null}

            <EditorSectionCard
              title={t('navigation.resources')}
              actions={
                <div className="av-inline-metadata">
                  <button className="av-secondary-button" type="button" onClick={handleCancel} disabled={!isDirty || isSaving}>
                    {t('common.cancel')}
                  </button>
                  <button className="av-primary-button" type="button" onClick={() => void handleSave()} disabled={!isDirty || isSaving}>
                    {t('common.applyChanges')}
                  </button>
                </div>
              }
            >
              <div className="av-form-grid">
                <label className="av-form-field">
                  <span>{t('quickAdd.fields.name')}</span>
                  <TextField value={draft.name} onValueChange={(value) => setDraft((current) => (current ? { ...current, name: value } : current))} />
                </label>
                <label className="av-form-field">
                  <span>{t('quickAdd.fields.category')}</span>
                  <SelectField value={draft.kind} options={kindOptions} onChange={(event) => setDraft((current) => (current ? { ...current, kind: event.target.value as AssetKind } : current))} />
                </label>
              </div>

              <div className="av-form-grid">
                <label className="av-form-field">
                  <span>{t('quickAdd.fields.project')}</span>
                  <SelectField value={draft.projectId} options={projectOptions} onChange={(event) => setDraft((current) => (current ? { ...current, projectId: event.target.value } : current))} />
                </label>
                <label className="av-form-field">
                  <span>{t('quickAdd.fields.customer')}</span>
                  <SelectField value={draft.customerId} options={customerOptions} onChange={(event) => setDraft((current) => (current ? { ...current, customerId: event.target.value } : current))} />
                </label>
              </div>

              <div className="av-form-grid">
                <label className="av-form-field">
                  <span>{t('quickAdd.fields.mimeType')}</span>
                  <TextField value={draft.mimeType} onValueChange={(value) => setDraft((current) => (current ? { ...current, mimeType: value } : current))} />
                </label>
                <label className="av-form-field">
                  <span>{t('quickAdd.fields.storagePath')}</span>
                  <TextField value={draft.storagePath} onValueChange={(value) => setDraft((current) => (current ? { ...current, storagePath: value } : current))} />
                </label>
              </div>
            </EditorSectionCard>
          </>
        }
      />
    </EditorPageLayout>
  );
}
